import { ReactNode, useEffect, MouseEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { HeadingS, TextS } from '@/core/components/Typography.ts';
import PrimaryButton from '@/core/components/PrimaryButton.tsx';
import Icon from '@/core/components/Icon.tsx';

export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children?: ReactNode;
	listItems?: string[];
	image?: {
		src: string;
		alt?: string;
		backgroundColor?: string;
	};
	ctaText?: string;
	onCta?: () => void;
	ctaIcon?: string;
	footer?: ReactNode;
}

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(15, 15, 65, 0.4);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	padding: 0;

	@media (min-width: 768px) {
		padding: 16px;
	}
`;

const ModalContainer = styled.div`
	background: var(--Primary-white, white);
	width: 100%;
	height: 100%;
	overflow-y: auto;
	position: relative;
	display: flex;
	flex-direction: column;
	box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);

	@media (min-width: 768px) {
		max-width: 602px;
		max-height: 90vh;
		height: auto;
		border-radius: 24px;
	}
`;

const ImageContainer = styled.div<{ $bgColor?: string }>`
	width: 100%;
	height: 128px;
	background-color: ${(props) => props.$bgColor ?? 'var(--Orange-40, #FFAF47)'};
	display: flex;
	justify-content: center;
	align-items: center;
	overflow: hidden;
	flex-shrink: 0;
	padding: 8px;

	@media (min-width: 768px) {
		height: 216px;
	}

	img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
`;

const ContentWrapper = styled.div`
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 24px;
	height: 100%;

	@media (min-width: 768px) {
		padding: 32px;
	}
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const StyledHeadingS = styled(HeadingS)`
	max-width: 392px;
	text-overflow: ellipsis;
	overflow: clip;
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
	position: absolute;
	top: 16px;
	right: 16px;
	width: 40px;
	height: 40px;
	padding: 10px;
	cursor: pointer;
	color: var(--Primary-squla-dark-blue);
	display: flex;
	align-items: center;
	justify-content: center;
	justify-self: flex-end;
	flex-shrink: 0;
	border-radius: 1000px;
	border: 1px solid var(--opacities-dark-blue-10-divider);
	background: var(--Primary-white);
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const ListItem = styled.div`
	display: flex;
	gap: 8px;
`;

const StyledIcon = styled(Icon)`
	color: var(--Primary-squla-blue);
	flex-shrink: 0;
`;

const ResponsiveTextS = styled(TextS)`
	@media (min-width: 768px) {
		font-size: 16px;
		line-height: 24px;
	}
`;

const Footer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	width: 100%;
	margin-top: auto;
`;

export function Modal({
	isOpen,
	onClose,
	title,
	children,
	listItems,
	image,
	ctaText,
	onCta,
	ctaIcon,
	footer,
}: ModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}

			if (e.key === 'Tab' && modalRef.current) {
				const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
				);
				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement) {
						lastElement.focus();
						e.preventDefault();
					}
				} else {
					if (document.activeElement === lastElement) {
						firstElement.focus();
						e.preventDefault();
					}
				}
			}
		};

		const rootElements = Array.from(document.body.children).filter(
			(el) => el.id !== 'modal-root' && el.tagName !== 'SCRIPT',
		);

		if (isOpen) {
			document.body.style.overflow = 'hidden';
			window.addEventListener('keydown', handleKeyDown);

			rootElements.forEach((el) => {
				el.setAttribute('aria-hidden', 'true');
			});

			// Focus the first focusable element or the container
			if (modalRef.current) {
				const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
				);
				if (focusableElements.length > 0) {
					focusableElements[0].focus();
				} else {
					modalRef.current.focus();
				}
			}
		}

		return () => {
			document.body.style.overflow = '';
			window.removeEventListener('keydown', handleKeyDown);
			rootElements.forEach((el) => {
				el.removeAttribute('aria-hidden');
			});
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const handleOverlayClick = (e: MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const modalContent = (
		<Overlay onClick={handleOverlayClick}>
			<ModalContainer
				ref={modalRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="modal-title"
				tabIndex={-1}
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				{image && (
					<ImageContainer $bgColor={image.backgroundColor}>
						<img src={image.src} alt={image.alt ?? ''} />
					</ImageContainer>
				)}
				<ContentWrapper>
					<CloseButton onClick={onClose} aria-label="Close modal">
						<Icon name="x-close" size={24} />
					</CloseButton>
					<Header>
						<StyledHeadingS id="modal-title">{title}</StyledHeadingS>
					</Header>
					<Body>
						{children && <ResponsiveTextS as="div">{children}</ResponsiveTextS>}
						{listItems && listItems.length > 0 && (
							<List>
								{listItems.map((item) => (
									<ListItem key={item}>
										<StyledIcon name="check" size={24} />
										<ResponsiveTextS>{item}</ResponsiveTextS>
									</ListItem>
								))}
							</List>
						)}
					</Body>
					{(ctaText ?? footer) && (
						<Footer>
							{footer}
							{ctaText && (
								<PrimaryButton onClick={onCta} icon={ctaIcon}>
									{ctaText}
								</PrimaryButton>
							)}
						</Footer>
					)}
				</ContentWrapper>
			</ModalContainer>
		</Overlay>
	);

	let modalRoot = document.getElementById('modal-root');
	if (!modalRoot) {
		const appRoot = document.getElementById('root');
		modalRoot = document.createElement('div');
		modalRoot.id = 'modal-root';
		(appRoot ?? document.body).appendChild(modalRoot);
	}

	return createPortal(modalContent, modalRoot);
}

export default Modal;
